import { useEffect, useState, useRef, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PanelState, { extractApiError } from "@/components/PanelState";
import MREServiceProMetrics from "./MREServiceProMetrics";
import WallMonthlyMode from "./WallMonthlyMode";
import { WallWeeklyPresentation, RevenueDrilldown } from "./PresentationViews";
import { Trophy, Maximize, Minimize, Target, Search, CornerDownLeft, ChevronLeft, ChevronRight, ChevronDown } from "lucide-react";

const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
// 2026-08-03 bug #3 fix: revenue displays MUST include cents (was showing
// $9,276.51 as "9,276."). Goals and OBP targets stay whole-dollar since
// they're set as round numbers by ops — mixing precision only where it
// reflects a real difference in the underlying data.
const fmt$c = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function useClock() {
  const [t, setT] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setT(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return t;
}

const STATUS_STYLES = {
  ON_TARGET: "bg-[#dcfce7] text-[#166534] border-[#4ade80] font-bold",
  WATCH:     "bg-[#fef3c7] text-[#854d0e] border-[#facc15] font-bold",
  OFF_TRACK: "bg-[#fee2e2] text-[#991b1b] border-[#f87171] font-bold",
  NO_GOAL:   "bg-[#e5e7eb] text-[#4b5563] border-[#9ca3af] font-bold",
};
function GoalBadge({ status }) {
  const cls = STATUS_STYLES[status] || STATUS_STYLES.NO_GOAL;
  const label = status === "NO_GOAL" ? "NO GOAL SET" : status.replace(/_/g, " ");
  return (
    <span className={`inline-flex text-[10px] font-mono-data uppercase tracking-widest border rounded px-2 py-0.5 ${cls}`}>
      {label}
    </span>
  );
}

function toneForCompliance(pct) {
  if (pct >= 75) return { text: "text-[#166534]", bar: "bg-[#166534]" };
  if (pct >= 50) return { text: "text-[#854d0e]", bar: "bg-[#eab308]" };
  return { text: "text-[#991b1b]", bar: "bg-[#dc2626]" };
}

// Meeting-attendance pill. Cycles Not marked (null) → Present (true) →
// Absent (false) → Not marked. Non-editors get a read-only pill. Saves
// happen immediately; failures roll back locally + surface an inline hint.
function AttendancePill({ techName, present, weekOf, canEdit, onSaved }) {
  const [local, setLocal] = useState(present);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => { setLocal(present); }, [present, weekOf]);

  const nextState = (v) => (v === null || v === undefined ? true : v === true ? false : null);

  const cycle = async () => {
    if (!canEdit || saving) return;
    const target = nextState(local);
    const prev = local;
    setLocal(target);
    setSaving(true);
    setErr(null);
    try {
      await api.patch("/kpi/meeting-attendance", {
        tech_name: techName,
        present: target,
        week_of: weekOf,
      });
      onSaved?.();
    } catch (e) {
      setLocal(prev);
      setErr(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const styles = local === true
    ? "bg-[#dcfce7] text-[#166534] border-[#4ade80]"
    : local === false
    ? "bg-[#fee2e2] text-[#991b1b] border-[#f87171]"
    : "bg-[#f5f6f8] text-[#4b5563] border-[#cbd5e1] border-dashed";
  const label = local === true ? "Present" : local === false ? "Absent" : "Not marked";

  return (
    <button
      type="button"
      onClick={cycle}
      disabled={!canEdit || saving}
      title={canEdit ? `Click to cycle: ${label} → ${nextState(local) === null ? "Not marked" : nextState(local) ? "Present" : "Absent"}` : label}
      data-testid={`wall-attendance-${techName}`}
      data-attendance-state={local === true ? "present" : local === false ? "absent" : "unmarked"}
      className={`text-[11.5px] font-mono-data font-bold rounded-full px-3 py-1 border transition ${styles} ${
        canEdit ? "cursor-pointer hover:brightness-95 active:scale-[0.98]" : "cursor-default"
      } ${saving ? "opacity-60" : ""}`}
    >
      {techName} · {label}
      {err && <span className="ml-1 text-[9px] text-[#991b1b]">· {err}</span>}
    </button>
  );
}

// Inline editable weekly goal field. Blank → clears the goal (NO GOAL SET).
// `weekOfIso` — when provided, targets that specific week (used for "Next Week")
function GoalInput({ techName, weeklyGoal, hasGoal, weekOfIso, testIdPrefix = "wall-goal", onSaved }) {
  const [val, setVal] = useState(hasGoal ? String(weeklyGoal) : "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => { setVal(hasGoal ? String(weeklyGoal) : ""); }, [weeklyGoal, hasGoal]);

  const commit = async () => {
    setErr(null);
    const trimmed = val.trim();
    // no-op if unchanged
    const original = hasGoal ? String(weeklyGoal) : "";
    if (trimmed === original) return;
    const payload = trimmed === "" ? null : Number(trimmed);
    if (trimmed !== "" && (Number.isNaN(payload) || payload < 0)) {
      setErr("Must be a positive number");
      return;
    }
    setSaving(true);
    try {
      const body = { tech_name: techName, weekly_goal: payload };
      if (weekOfIso) body.week_of = weekOfIso;
      await api.patch("/tech-goals", body);
      onSaved?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center gap-1.5">
      <div className="relative">
        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[11px] font-mono-data text-[color:var(--cc-ink-muted)]">$</span>
        <input
          type="number"
          inputMode="numeric"
          min="0"
          step="100"
          value={val}
          placeholder="not set"
          onChange={(e) => setVal(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
          data-testid={`${testIdPrefix}-input-${techName}`}
          className="w-28 bg-white border border-[#e7e9ee] rounded pl-5 pr-2 py-1 text-[13px] font-mono-data font-semibold text-[#0e1420] text-right outline-none focus:border-[#0072bc] disabled:opacity-40"
          disabled={saving}
        />
      </div>
      {err && <span className="text-[10.5px] text-[#991b1b]" data-testid={`${testIdPrefix}-error-${techName}`}>{err}</span>}
    </div>
  );
}

// Bulk "Set Next Week's Goals" panel — foldable, one editable input per tech.
// Reads existing next-week goals from the same `techs[]` payload, writes via /tech-goals with next_week_of.
function BulkNextWeekGoals({ techs, nextWeekOf, nextWeekRange, weekOf, weekLabel, onSaved }) {
  const [open, setOpen] = useState(false);
  const [copying, setCopying] = useState(false);
  const [copyResult, setCopyResult] = useState(null);
  const [copyError, setCopyError] = useState(null);
  const unsetCount = techs.filter((t) => !t.has_next_goal).length;
  const anyNextSet = techs.some((t) => t.has_next_goal);
  const anyThisSet = techs.some((t) => t.has_goal);

  const doCopy = async () => {
    setCopyError(null);
    setCopyResult(null);
    if (anyNextSet) {
      const ok = window.confirm(
        "Some techs already have a Next Week goal set. Overwrite them?\n\nCancel to skip existing goals (only copy for techs whose next-week goal is not set)."
      );
      if (ok === null) return;
      await runCopy(ok);
    } else {
      await runCopy(false);
    }
  };
  const runCopy = async (overwrite) => {
    setCopying(true);
    try {
      const { data } = await api.post("/tech-goals/copy", {
        from_week_of: weekOf,
        to_week_of: nextWeekOf,
        overwrite,
      });
      setCopyResult(data);
      onSaved?.();
    } catch (e) {
      setCopyError(e?.response?.data?.detail || "Copy failed");
    } finally {
      setCopying(false);
    }
  };

  return (
    <section data-testid="wall-next-week-goals" className="bg-white border border-[#e7e9ee] rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="wall-next-week-toggle"
        className="w-full flex items-center justify-between gap-3 px-5 py-3 border-b border-transparent hover:bg-[#fafbfc]"
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="w-1 h-4 rounded-sm bg-[#FFC72C]" />
          <span className="text-[11px] uppercase tracking-[.09em] text-[#0e1420] font-bold">
            Set Next Week&apos;s Goals
          </span>
          <span className="text-[11px] uppercase tracking-[.09em] text-[color:var(--cc-ink-muted)] font-semibold">
            · {nextWeekRange} · {unsetCount} unset
          </span>
        </div>
        <span className="text-[11.5px] text-[color:var(--cc-ink-muted)] font-semibold">
          {open ? "Hide" : "Bulk set"}
        </span>
      </button>
      {open && (
        <div className="border-t border-[#eef1f6]">
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 bg-[#fafbfc] border-b border-[#eef1f6]">
            <div className="text-[12px] text-[color:var(--cc-ink-muted)]">
              Copy every set goal from <b className="text-[#0e1420]">{weekLabel || "This Week"}</b> into <b className="text-[#0e1420]">Next Week</b>.
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {copyResult && (
                <span className="text-[11.5px] font-mono-data text-[#166534]" data-testid="wall-copy-result">
                  Copied {copyResult.copied.length}
                  {copyResult.skipped.length > 0 ? ` · skipped ${copyResult.skipped.length}` : ""}
                </span>
              )}
              {copyError && (
                <span className="text-[11.5px] text-[#991b1b]" data-testid="wall-copy-error">{copyError}</span>
              )}
              <button
                type="button"
                onClick={doCopy}
                disabled={copying || !anyThisSet}
                data-testid="wall-copy-goals-btn"
                title={!anyThisSet ? "Set at least one goal for this week before copying" : "Copy this week's goals into next week"}
                className="text-[12px] font-semibold px-3 py-1.5 rounded-md bg-[#0072bc] text-white hover:bg-[#005c99] disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
              >
                {copying ? "Copying…" : "Copy this week → next week"}
              </button>
            </div>
          </div>
          <div className="p-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {techs.map((t) => (
              <div key={t.tech_name}
                   data-testid={`wall-next-week-row-${t.tech_name}`}
                   className={`flex items-center justify-between gap-3 border border-[#eef1f6] rounded-lg px-3 py-2 bg-[#fafbfc] ${t.no_jobs ? "opacity-60" : ""}`}>
                <div className="min-w-0">
                  <div className="text-[13px] font-semibold text-[#0e1420] truncate">{t.tech_name}</div>
                  <div className="text-[10.5px] text-[color:var(--cc-ink-muted)] truncate">
                    {t.unit}{t.no_jobs ? " · no jobs this week" : ""}
                  </div>
                </div>
                <GoalInput
                  techName={t.tech_name}
                  weeklyGoal={t.next_weekly_goal}
                  hasGoal={t.has_next_goal}
                  weekOfIso={nextWeekOf}
                  testIdPrefix="wall-next-goal"
                  onSaved={onSaved}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

// Ctrl+K quick tech-picker palette. Fuzzy-ish filter (case-insensitive substring),
// arrow keys + Enter to select, Esc to close. Selecting jumps focus to that tech's
// weekly-goal input (this-week or next-week, whichever the user picks).
function TechPickerPalette({ open, techs, weekLabel, onClose }) {
  const [q, setQ] = useState("");
  const [target, setTarget] = useState("this"); // "this" | "next"
  const [idx, setIdx] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQ("");
      setIdx(0);
      setTarget("this");
      // focus input on next tick so autoFocus works after modal mounts
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return techs;
    return techs.filter((t) => t.tech_name.toLowerCase().includes(s));
  }, [q, techs]);

  useEffect(() => { setIdx(0); }, [q, target]);

  if (!open) return null;

  const pick = (tech) => {
    if (!tech) return;
    const testId = target === "next"
      ? `wall-next-goal-inline-input-${tech.tech_name}`
      : `wall-goal-input-${tech.tech_name}`;
    onClose();
    // let the modal unmount before we scroll+focus
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="${testId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.focus();
        try { el.select(); } catch { /* ignore for number inputs on some browsers */ }
      }
    }, 40);
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape") { e.preventDefault(); onClose(); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setIdx((i) => Math.min(filtered.length - 1, i + 1)); return; }
    if (e.key === "ArrowUp") { e.preventDefault(); setIdx((i) => Math.max(0, i - 1)); return; }
    if (e.key === "Tab") { e.preventDefault(); setTarget((t) => (t === "this" ? "next" : "this")); return; }
    if (e.key === "Enter") { e.preventDefault(); pick(filtered[idx]); return; }
  };

  return (
    <div
      className="fixed inset-0 bg-black/45 backdrop-blur-sm z-50 flex items-start justify-center p-4 pt-[15vh]"
      onClick={onClose}
      data-testid="wall-tech-picker"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl w-full max-w-[540px] shadow-2xl overflow-hidden border border-[#e7e9ee]"
      >
        <div className="flex items-center gap-2 px-4 py-3 border-b border-[#eef1f6]">
          <Search size={16} strokeWidth={1.8} className="text-[color:var(--cc-ink-muted)]" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Find a tech…"
            data-testid="wall-tech-picker-input"
            className="flex-1 outline-none text-[14px] text-[#0e1420] placeholder:text-[color:var(--cc-ink-muted)] bg-transparent"
          />
          <div className="inline-flex bg-[#f5f6f8] rounded-md p-0.5 border border-[#e7e9ee]" data-testid="wall-tech-picker-target">
            {[
              { id: "this", label: weekLabel || "This Week" },
              { id: "next", label: "Next Week" },
            ].map((o) => (
              <button
                key={o.id}
                type="button"
                onClick={() => setTarget(o.id)}
                data-testid={`wall-tech-picker-target-${o.id}`}
                className={`px-2 py-1 text-[10.5px] rounded-sm font-semibold uppercase tracking-widest transition-colors ${
                  target === o.id ? "bg-white text-[#0072bc] shadow-sm" : "text-[color:var(--cc-ink-muted)]"
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
        <div className="max-h-[50vh] overflow-y-auto" data-testid="wall-tech-picker-list">
          {filtered.length === 0 ? (
            <div className="px-4 py-6 text-center text-[13px] text-[color:var(--cc-ink-muted)]">
              No matching tech.
            </div>
          ) : filtered.map((t, i) => {
            const isActive = i === idx;
            return (
              <button
                key={t.tech_name}
                type="button"
                onMouseEnter={() => setIdx(i)}
                onClick={() => pick(t)}
                data-testid={`wall-tech-picker-row-${t.tech_name}`}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-left border-b border-[#eef1f6] last:border-0 ${
                  isActive ? "bg-[#e7f4fb]" : "bg-white"
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-[13.5px] font-semibold text-[#0e1420] truncate">
                    {t.tech_name}
                    {t.no_jobs && (
                      <span className="ml-2 text-[9px] font-mono-data uppercase tracking-widest text-[#4b5563]">
                        · no jobs
                      </span>
                    )}
                  </div>
                  <div className="text-[10.5px] font-mono-data text-[color:var(--cc-ink-muted)] truncate">
                    {t.unit} · goal {target === "next"
                      ? (t.has_next_goal ? `$${Number(t.next_weekly_goal).toLocaleString(undefined, {maximumFractionDigits: 0})}` : "not set")
                      : (t.has_goal ? `$${Number(t.weekly_goal).toLocaleString(undefined, {maximumFractionDigits: 0})}` : "not set")}
                  </div>
                </div>
                {isActive && <CornerDownLeft size={13} strokeWidth={1.8} className="text-[#0072bc]" />}
              </button>
            );
          })}
        </div>
        <div className="px-4 py-2 border-t border-[#eef1f6] bg-[#fafbfc] flex items-center justify-between text-[10.5px] font-mono-data uppercase tracking-widest text-[color:var(--cc-ink-muted)]">
          <span>↑↓ move · ↵ pick · Tab switch week · Esc close</span>
          <span>Ctrl/⌘ + K</span>
        </div>
      </div>
    </div>
  );
}

export default function WallMode() {
  const { user } = useAuth();
  const canEditAttendance = !!user && ["platform_admin", "owner", "administrative", "gm_ops", "call_center", "dispatch_csr"].includes(user.role);
  const [viewMode, setViewMode] = useState("weekly"); // "weekly" | "monthly"
  const [month, setMonth] = useState(null); // null = current month
  const [availableMonths, setAvailableMonths] = useState([]);
  // CC-003 (2026-09-22): URL-synced week state so a Wall view can be
  // linked, bookmarked, or opened in Present mode with the same target
  // week. `?week=YYYY-MM-DD` wins over the default "this".
  const [searchParams, setSearchParams] = useSearchParams();
  const urlWeekParam = searchParams.get("week");
  const isIsoWeek = urlWeekParam && /^\d{4}-\d{2}-\d{2}$/.test(urlWeekParam);
  const [week, setWeekState] = useState(isIsoWeek ? urlWeekParam : "this");
  // Wrapper that keeps URL and state in lockstep so Present mode +
  // bookmark links honor the same target week without stale drift.
  const setWeek = (next) => {
    setWeekState(next);
    const params = new URLSearchParams(searchParams);
    if (next && next !== "this") params.set("week", next);
    else params.delete("week");
    setSearchParams(params, { replace: true });
  };
  const [availableWeeks, setAvailableWeeks] = useState([]); // [{week_of, label, is_this, is_last, job_count, has_data}]
  const [pickerOpen, setPickerOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loadState, setLoadState] = useState({ status: "loading", error: null });
  const clock = useClock();
  const wrapRef = useRef(null);
  const [fs, setFs] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const hasLoadedOnceRef = useRef(false);

  const load = async (w) => {
    // Only show the full-screen PanelState skeleton on the very first fetch.
    // Background refetches (after a save/copy) keep the last-good data mounted
    // so children (Bulk panel result text, palette state, focus) survive.
    if (!hasLoadedOnceRef.current) setLoadState({ status: "loading", error: null });
    try {
      const { data: resp } = await api.get(`/kpi/wall?week=${w}`);
      setData(resp);
      setLoadState({ status: "ok", error: null });
      hasLoadedOnceRef.current = true;
    } catch (err) {
      // On a background refetch failure keep the last-good `data` and just surface a toast-style hint
      if (!hasLoadedOnceRef.current) {
        setData(null);
        setLoadState({ status: "error", error: extractApiError(err) });
      }
    }
  };
  useEffect(() => {
    hasLoadedOnceRef.current = false;
    load(week);
     
  }, [week]);

  // 2026-08-04: fetch the list of available past weeks so the picker has
  // real data-backed options. Refreshes any time the loaded `data` changes
  // (new ingest could add a fresh week between session opens).
  useEffect(() => {
    let cancelled = false;
    api.get("/kpi/available-weeks").then(({ data: resp }) => {
      if (!cancelled) setAvailableWeeks(resp?.weeks || []);
    }).catch(() => { /* dropdown just stays empty; This/Last still work */ });
    return () => { cancelled = true; };
  }, [data?.week_of]);

  // 2026-08-10: months list for the Monthly-mode picker. Same graceful-fallback
  // pattern as the weeks list.
  useEffect(() => {
    let cancelled = false;
    api.get("/kpi/available-months").then(({ data: resp }) => {
      if (!cancelled) setAvailableMonths(resp?.months || []);
    }).catch(() => { /* Monthly mode still resolves current month server-side */ });
    return () => { cancelled = true; };
  }, [viewMode]);

  // Resolve `week` (which may be "this"/"last" alias OR an ISO date) to the
  // ISO week_of currently loaded, so the picker + prev/next arrows can
  // reason about position within the available-weeks list.
  const currentWeekOf = data?.week_of || null;
  const currentIdx = availableWeeks.findIndex((w) => w.week_of === currentWeekOf);
  // CC-003: arrows must SKIP empty weeks (has_data === false). Find the
  // next/prev index that actually has data before enabling the button.
  const nextNewerIdx = (() => {
    if (currentIdx < 0) return -1;
    for (let i = currentIdx - 1; i >= 0; i -= 1) {
      if (availableWeeks[i]?.has_data) return i;
    }
    return -1;
  })();
  const nextOlderIdx = (() => {
    if (currentIdx < 0) return -1;
    for (let i = currentIdx + 1; i < availableWeeks.length; i += 1) {
      if (availableWeeks[i]?.has_data) return i;
    }
    return -1;
  })();
  const canGoOlder = nextOlderIdx > -1;
  const canGoNewer = nextNewerIdx > -1;
  const goOlder = () => { if (canGoOlder) setWeek(availableWeeks[nextOlderIdx].week_of); };
  const goNewer = () => { if (canGoNewer) setWeek(availableWeeks[nextNewerIdx].week_of); };
  // CC-003: Today button — always returns to This Week regardless of
  // whether the user typed an ISO into the URL bar or navigated deep
  // into history. Uses the alias "this" so the backend resolves the
  // current Monday against the tenant's timezone (America/Chicago).
  const thisWeekIso = availableWeeks.find((w) => w.is_this)?.week_of || null;
  const isOnThisWeek = currentWeekOf && thisWeekIso && currentWeekOf === thisWeekIso;
  const goToday = () => { setWeek("this"); };
  const pickerLabel = currentWeekOf
    ? (availableWeeks.find((w) => w.week_of === currentWeekOf)?.label || data?.week_range || currentWeekOf)
    : (week === "last" ? "Last Week" : "This Week");

  // Present toggle: try native browser fullscreen; if unavailable (iframe sandbox,
  // browser rejection), fall back to CSS pseudo-fullscreen so the button always works.
  const toggleFullscreen = () => {
    const el = wrapRef.current;
    // If we're already in either mode, exit
    if (document.fullscreenElement) {
      document.exitFullscreen?.().then(() => setFs(false)).catch(() => setFs(false));
      return;
    }
    if (fs) {
      setFs(false);
      return;
    }
    // Enter: try native, fall back to CSS
    const req = el?.requestFullscreen || el?.webkitRequestFullscreen || el?.msRequestFullscreen;
    if (req) {
      try {
        const p = req.call(el);
        if (p && typeof p.then === "function") {
          p.then(() => setFs(true)).catch(() => setFs(true));
          return;
        }
        setFs(true);
        return;
      } catch {
        setFs(true);
        return;
      }
    }
    setFs(true);
  };
  useEffect(() => {
    const on = () => {
      // If browser fullscreen exited (e.g., user hit Esc), reflect it in state
      if (!document.fullscreenElement) setFs((prev) => (prev ? prev : false));
    };
    document.addEventListener("fullscreenchange", on);
    return () => document.removeEventListener("fullscreenchange", on);
  }, []);

  // Esc exits the CSS-fallback pseudo-fullscreen (native fullscreen exits itself).
  useEffect(() => {
    if (!fs) return;
    const onEsc = (e) => {
      if (e.key === "Escape" && !document.fullscreenElement) setFs(false);
    };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [fs]);

  // Ctrl/Cmd+K opens the quick tech-picker palette (Monday-meeting shortcut).
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const isEmpty = loadState.status === "ok" && (!data || !data.techs || data.techs.length === 0);
  if (loadState.status !== "ok" || isEmpty) {
    return (
      <PanelState
        status={loadState.status}
        error={loadState.error}
        isEmpty={isEmpty}
        onRetry={() => load(week)}
        emptyTitle="Wall has no data for this week"
        emptyBody={<>Switch weeks or wait for today&apos;s jobs to sync from ServiceTitan.</>}
        testIdPrefix="kpi-wall"
      />
    );
  }

  const tp = data.top_performer;
  const maxRev = Math.max(...data.weekly_revenue.map((r) => r.revenue), 1);
  const noGoalCount = data.techs.filter((t) => !t.has_goal).length;

  // Presentation-mode short-circuit — when Full Screen is on, render the
  // scoreboard layout instead of the desk layout. The fullscreen container
  // + exit toggle stay so the user can flip back.
  if (fs) {
    return (
      <div ref={wrapRef} data-testid="kpi-wall-mode" className="fixed inset-0 z-40 bg-white p-8 overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="inline-flex bg-white rounded-lg p-1 border-2 border-[#0e1420]">
            <button
              type="button"
              onClick={() => setViewMode("weekly")}
              data-testid="wall-view-mode-weekly"
              className={`px-6 py-3 text-[18px] rounded-md font-bold ${viewMode === "weekly" ? "bg-[#0e1420] text-white" : "text-[#0e1420]"}`}
            >
              Weekly
            </button>
            <button
              type="button"
              onClick={() => setViewMode("monthly")}
              data-testid="wall-view-mode-monthly"
              className={`px-6 py-3 text-[18px] rounded-md font-bold ${viewMode === "monthly" ? "bg-[#0e1420] text-white" : "text-[#0e1420]"}`}
            >
              Monthly
            </button>
          </div>
          <button
            onClick={toggleFullscreen}
            data-testid="wall-fullscreen-exit"
            className="inline-flex items-center gap-2 text-[16px] font-bold px-4 py-3 rounded-lg border-2 border-[#0e1420] bg-white text-[#0e1420] hover:bg-[#f5f6f8]"
          >
            <Minimize size={18} strokeWidth={2} />
            Exit Full Screen
          </button>
        </div>
        {viewMode === "weekly"
          ? <WallWeeklyPresentation data={data} onSaved={() => load(week)} />
          : <WallMonthlyMode month={month} setMonth={setMonth} availableMonths={availableMonths} currentMonthOf={availableMonths.find((m) => m.is_this)?.month || null} fullscreen />}
      </div>
    );
  }

  return (
    <div ref={wrapRef} data-testid="kpi-wall-mode" className={fs ? "fixed inset-0 z-40 bg-white p-6 overflow-y-auto" : ""}>
      <div className="space-y-5">
        {/* Header */}
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-[.15em] text-[#0072bc] font-bold">
              Monday Meeting Wall
            </div>
            <h1 className="font-display font-bold text-[26px] sm:text-[32px] tracking-tight text-[#0e1420] mt-1">
              {viewMode === "weekly" ? data.week_range : "Monthly roll-up"}
            </h1>
            <p className="text-[13.5px] text-[color:var(--cc-ink-muted)] mt-1">
              {viewMode === "weekly"
                ? "Set each tech's weekly goal below during the meeting. Everything else on this app reads from these values."
                : "Monthly totals aggregate the weekly Wall — goals default to the sum of your weekly goals for that month. Type in the Override column to lock a stretch target."}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <div className="font-mono-data text-[13px] text-[color:var(--cc-ink-muted)]">
              {clock.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
            </div>
            {/* Weekly / Monthly view toggle (2026-08-10, Monday-presentation macro roll-up) */}
            <div className="inline-flex bg-white rounded-lg p-0.5 border border-[#e7e9ee]" data-testid="wall-view-mode-switch">
              <button
                type="button"
                onClick={() => setViewMode("weekly")}
                data-testid="wall-view-mode-weekly"
                className={`px-3 py-1.5 text-[11.5px] rounded-md font-semibold ${
                  viewMode === "weekly" ? "bg-[#0072bc] text-white" : "text-[#0e1420] hover:bg-[#f5f6f8]"
                }`}
              >
                Weekly
              </button>
              <button
                type="button"
                onClick={() => setViewMode("monthly")}
                data-testid="wall-view-mode-monthly"
                className={`px-3 py-1.5 text-[11.5px] rounded-md font-semibold ${
                  viewMode === "monthly" ? "bg-[#0072bc] text-white" : "text-[#0e1420] hover:bg-[#f5f6f8]"
                }`}
              >
                Monthly
              </button>
            </div>
            {viewMode === "weekly" && (
            <button
              type="button"
              onClick={goToday}
              disabled={isOnThisWeek}
              data-testid="wall-week-today"
              title={isOnThisWeek ? "Already viewing This Week" : "Jump back to This Week"}
              className={`px-3 py-1.5 text-[11.5px] rounded-md font-semibold border ${
                isOnThisWeek
                  ? "border-[#e7e9ee] text-[#c9cfd8] bg-white cursor-not-allowed"
                  : "border-[#0072bc] text-[#0072bc] bg-white hover:bg-[#e7f4fb]"
              }`}
            >
              Today
            </button>
            )}
            {viewMode === "weekly" && (
            <div
              className="inline-flex items-center gap-1 bg-white rounded-lg p-1 border border-[#e7e9ee] relative"
              data-testid="wall-week-switch"
            >
              <button
                type="button"
                onClick={goNewer}
                disabled={!canGoNewer}
                data-testid="wall-week-newer"
                title="Newer week"
                className={`p-1.5 rounded-md ${canGoNewer ? "hover:bg-[#f5f6f8] text-[#0e1420]" : "text-[#c9cfd8] cursor-not-allowed"}`}
              >
                <ChevronLeft size={14} strokeWidth={2.2} />
              </button>
              <button
                type="button"
                onClick={() => setPickerOpen((o) => !o)}
                data-testid="wall-week-picker"
                className="px-3 py-1.5 text-[11.5px] rounded-md font-semibold text-[#0e1420] hover:bg-[#f5f6f8] inline-flex items-center gap-1.5 min-w-[140px] justify-center"
              >
                <span>{pickerLabel}</span>
                <ChevronDown size={12} strokeWidth={2.2} className="opacity-60" />
              </button>
              <button
                type="button"
                onClick={goOlder}
                disabled={!canGoOlder}
                data-testid="wall-week-older"
                title="Older week"
                className={`p-1.5 rounded-md ${canGoOlder ? "hover:bg-[#f5f6f8] text-[#0e1420]" : "text-[#c9cfd8] cursor-not-allowed"}`}
              >
                <ChevronRight size={14} strokeWidth={2.2} />
              </button>
              {pickerOpen && (
                <>
                  <div
                    className="fixed inset-0 z-30"
                    onClick={() => setPickerOpen(false)}
                  />
                  <div
                    data-testid="wall-week-picker-dropdown"
                    className="absolute top-full right-0 mt-1 bg-white border border-[#e7e9ee] rounded-lg shadow-lg z-40 min-w-[240px] max-h-[360px] overflow-y-auto"
                  >
                    {/* Quick-access shortcuts */}
                    <button
                      type="button"
                      onClick={() => { setWeek("this"); setPickerOpen(false); }}
                      data-testid="wall-week-shortcut-this"
                      className="w-full text-left px-3 py-2 text-[12px] hover:bg-[#f5f6f8] border-b border-[#eef1f6] font-semibold"
                    >
                      This Week
                    </button>
                    <button
                      type="button"
                      onClick={() => { setWeek("last"); setPickerOpen(false); }}
                      data-testid="wall-week-shortcut-last"
                      className="w-full text-left px-3 py-2 text-[12px] hover:bg-[#f5f6f8] border-b border-[#eef1f6] font-semibold"
                    >
                      Last Week
                    </button>
                    {availableWeeks.length === 0 && (
                      <div className="px-3 py-2 text-[11.5px] text-[color:var(--cc-ink-muted)] italic">
                        No historical weeks yet
                      </div>
                    )}
                    {availableWeeks.map((w) => {
                      const isEmpty = w.has_data === false || w.job_count === 0;
                      return (
                      <button
                        key={w.week_of}
                        type="button"
                        onClick={() => { if (!isEmpty) { setWeek(w.week_of); setPickerOpen(false); } }}
                        disabled={isEmpty}
                        data-testid={`wall-week-opt-${w.week_of}`}
                        data-empty={isEmpty ? "true" : "false"}
                        title={isEmpty ? "No kpi_jobs recorded for this week" : `Jump to ${w.label}`}
                        className={`w-full text-left px-3 py-2 text-[12px] flex items-center justify-between ${
                          isEmpty
                            ? "text-[#c9cfd8] cursor-not-allowed bg-[#fafbfc]"
                            : "hover:bg-[#f5f6f8] " + (w.week_of === currentWeekOf ? "bg-[#e7f4fb] text-[#0072bc] font-semibold" : "text-[#0e1420]")
                        }`}
                      >
                        <span>
                          {w.label}
                          {w.is_this && <span className="ml-2 text-[10px] uppercase tracking-widest text-[#0072bc]">This</span>}
                          {w.is_last && <span className="ml-2 text-[10px] uppercase tracking-widest text-[color:var(--cc-ink-muted)]">Last</span>}
                        </span>
                        <span className="text-[10.5px] font-mono-data text-[color:var(--cc-ink-muted)]">{w.job_count} jobs</span>
                      </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
            )}
            {viewMode === "weekly" && (
            <button
              onClick={() => setPaletteOpen(true)}
              data-testid="wall-tech-picker-btn"
              title="Quick tech picker (Ctrl/⌘+K)"
              className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-md border border-[#e7e9ee] bg-white text-[#0e1420] hover:bg-[#f5f6f8]"
            >
              <Search size={13} strokeWidth={1.8} />
              <span>Find tech</span>
              <kbd className="ml-1 text-[9.5px] font-mono-data uppercase tracking-widest bg-[#f5f6f8] text-[color:var(--cc-ink-muted)] border border-[#e7e9ee] rounded px-1 py-[1px]">⌘K</kbd>
            </button>
            )}
            <button
              onClick={toggleFullscreen}
              data-testid="wall-fullscreen-btn"
              title={fs ? "Exit fullscreen" : "Present fullscreen"}
              className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-md border border-[#e7e9ee] bg-white text-[#0e1420] hover:bg-[#f5f6f8]"
            >
              {fs ? <Minimize size={13} strokeWidth={1.8} /> : <Maximize size={13} strokeWidth={1.8} />}
              {fs ? "Exit" : "Present"}
            </button>
          </div>
        </header>

        {viewMode === "monthly" && (
          <WallMonthlyMode
            month={month}
            setMonth={setMonth}
            availableMonths={availableMonths}
            currentMonthOf={availableMonths.find((m) => m.is_this)?.month || null}
          />
        )}

        {viewMode === "weekly" && (<>
        {/* Missing-goal callout */}
        {noGoalCount > 0 && (
          <div className="bg-[#fef3c7] border border-[#facc15] rounded-xl p-4 flex items-start gap-3" data-testid="wall-missing-goals-callout">
            <Target size={18} strokeWidth={1.8} className="text-[#854d0e] mt-0.5" />
            <div className="text-[13px] text-[#854d0e]">
              <b>{noGoalCount} tech{noGoalCount === 1 ? "" : "s"} have no goal set for this week.</b>{" "}
              Enter each tech&apos;s stated goal inline below. KPI Tracker Entry/Scorecard, and L10&apos;s scorecard + headlines all read from these values.
            </div>
          </div>
        )}

        {/* Top Performer */}
        {tp && (
          <div className="bg-white border border-[#e7e9ee] rounded-xl p-5 border-t-4 shadow-sm flex flex-wrap items-center gap-4"
               style={{ borderTopColor: "#FFC72C" }}
               data-testid="wall-top-performer">
            <div className="w-14 h-14 rounded-xl bg-[#FFC72C] text-[#0e1420] flex items-center justify-center">
              <Trophy size={26} strokeWidth={1.8} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10.5px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-bold">Top Performer — Revenue</div>
              <div className="font-display font-bold text-[24px] sm:text-[28px] text-[#0e1420] leading-tight">{tp.tech_name}</div>
              <div className="text-[12px] text-[color:var(--cc-ink-muted)]">{tp.unit}</div>
            </div>
            <div className="text-right">
              <div className="text-[11px] font-mono-data text-[color:var(--cc-ink-muted)] mb-1 uppercase tracking-widest">Revenue</div>
              <div className="font-mono-data font-bold text-[30px] sm:text-[34px] text-[#0072bc] leading-none" data-testid="wall-top-performer-revenue">{fmt$c(tp.revenue)}</div>
            </div>
          </div>
        )}

        {/* Tech Goals — the source-of-truth editing surface */}
        <section data-testid="wall-tech-goals">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-1 h-4 rounded-sm bg-[#0072bc]" />
            <span className="text-[11px] uppercase tracking-[.09em] text-[#0e1420] font-bold">
              Tech Goals · {data.week_label}
            </span>
            <span className="text-[11px] uppercase tracking-[.09em] text-[color:var(--cc-ink-muted)] font-semibold">
              · Editable inline · Source of truth
            </span>
            <span className="ml-auto text-[10px] font-mono-data uppercase tracking-widest bg-[#fff6dc] text-[#8a6a1c] border border-[#f0dfa5] rounded px-2 py-0.5 font-bold"
                  title="$2,500 weekly OBP baseline — kept separate from per-tech weekly revenue goals">
              OBP Floor $2,500 · fixed
            </span>
          </div>
          <div className="bg-white border border-[#e7e9ee] rounded-xl overflow-x-auto">
            <table className="w-full min-w-[860px] text-[13.5px]">
              <thead className="bg-[#f5f6f8] text-[11px] uppercase tracking-[.09em] text-[color:var(--cc-ink-muted)] font-bold">
                <tr>
                  <th className="text-left py-2.5 px-4">Technician</th>
                  <th className="text-left py-2.5 px-4">Unit</th>
                  <th className="text-right py-2.5 px-4">Revenue</th>
                  <th className="text-right py-2.5 px-4">Weekly Goal</th>
                  <th className="text-right py-2.5 px-4">% of Goal</th>
                  <th className="text-left py-2.5 px-4">Status</th>
                  <th className="text-right py-2.5 px-4 bg-[#fffbea] border-l border-[#f0dfa5]">Next Week Goal</th>
                </tr>
              </thead>
              <tbody>
                {data.techs.map((t) => {
                  const pctColor = t.pct_of_goal == null
                    ? "text-[color:var(--cc-ink-muted)]"
                    : t.pct_of_goal >= 100 ? "text-[#166534]"
                    : t.pct_of_goal >= 70 ? "text-[#854d0e]"
                    : "text-[#991b1b]";
                  const rowDim = t.no_jobs ? "opacity-70" : "";
                  return (
                    <tr key={t.tech_name} className={`border-t border-[#eef1f6] ${rowDim}`} data-testid={`wall-goal-row-${t.tech_name}`}>
                      <td className="py-2.5 px-4 font-semibold text-[#0e1420]">
                        {t.tech_name}
                        {t.no_jobs && (
                          <span className="ml-2 text-[9.5px] font-mono-data uppercase tracking-widest bg-[#e5e7eb] text-[#4b5563] border border-[#9ca3af] rounded px-1.5 py-0.5 font-bold align-middle">
                            no jobs
                          </span>
                        )}
                      </td>
                      <td className="py-2.5 px-4 text-[color:var(--cc-ink-muted)]">{t.unit}</td>
                      <td className="py-2.5 px-4 text-right">
                        <RevenueDrilldown techName={t.tech_name} revenue={t.revenue} jobs={t.revenue_jobs} testIdPrefix="wall"
                          className="font-mono-data font-semibold" />
                      </td>
                      <td className="py-2.5 px-4 text-right">
                        <div className="flex justify-end">
                          {/* CC-204: editable for every tech, every week; saves to the viewed week */}
                          <GoalInput
                            techName={t.tech_name}
                            weeklyGoal={t.weekly_goal}
                            hasGoal={t.has_goal}
                            weekOfIso={data.week_of}
                            testIdPrefix="wall-goal"
                            onSaved={() => load(week)}
                          />
                        </div>
                        {t.goal_carried_from && (
                          <div className="text-[10px] font-mono-data text-[color:var(--cc-ink-muted)] mt-0.5" data-testid={`wall-goal-carried-${t.tech_name}`}>
                            {t.goal_carried_from === "default" ? "platform default" : `carried from ${t.goal_carried_from}`}
                          </div>
                        )}
                      </td>
                      <td className={`py-2.5 px-4 text-right font-mono-data font-bold ${pctColor}`}>
                        {t.pct_of_goal != null ? `${t.pct_of_goal}%` : "—"}
                      </td>
                      <td className="py-2.5 px-4">
                        {t.no_goal_for_historical_week ? (
                          <span
                            data-testid={`wall-goal-badge-blank-${t.tech_name}`}
                            title="No goal set that week"
                            className="inline-flex text-[10px] font-mono-data uppercase tracking-widest border rounded px-2 py-0.5 bg-white text-[color:var(--cc-ink-muted)] border-dashed border-[#c9cfd8] cursor-help"
                          >
                            NO GOAL SET THAT WEEK
                          </span>
                        ) : (
                          <GoalBadge status={t.goal_status} />
                        )}
                      </td>
                      <td className="py-2.5 px-4 text-right bg-[#fffdf5] border-l border-[#f0dfa5]">
                        <div className="flex justify-end">
                          <GoalInput
                            techName={t.tech_name}
                            weeklyGoal={t.next_weekly_goal}
                            hasGoal={t.has_next_goal}
                            weekOfIso={data.next_week_of}
                            testIdPrefix="wall-next-goal-inline"
                            onSaved={() => load(week)}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        {/* Bulk-set Next Week Goals panel */}
        <BulkNextWeekGoals
          techs={data.techs}
          weekOf={data.week_of}
          weekLabel={data.week_label}
          nextWeekOf={data.next_week_of}
          nextWeekRange={data.next_week_range}
          onSaved={() => load(week)}
        />

        {/* Field Activity — KPI compliance bars */}
        <section data-testid="wall-field-activity">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-1 h-4 rounded-sm bg-[#0072bc]" />
            <span className="text-[11px] uppercase tracking-[.09em] text-[#0e1420] font-bold">Field Activity</span>
            <span className="text-[11px] uppercase tracking-[.09em] text-[color:var(--cc-ink-muted)] font-semibold">· KPI Compliance</span>
          </div>
          <div className="bg-white border border-[#e7e9ee] rounded-xl p-4 space-y-3">
            {data.techs.map((t) => {
              const tone = toneForCompliance(t.compliance_pct);
              return (
                <div key={t.tech_name} className={t.no_jobs ? "opacity-70" : ""}>
                  <div className="grid grid-cols-[130px_1fr_60px] items-center gap-3">
                    <div className="text-[13px] text-[#0e1420] truncate font-semibold">
                      {t.tech_name}
                      {t.no_jobs && <span className="ml-1.5 text-[9px] font-mono-data uppercase tracking-widest text-[#4b5563]">· no jobs</span>}
                    </div>
                    <div className="h-[10px] rounded-full bg-[#eef1f6] overflow-hidden">
                      <div className={`h-full ${tone.bar}`} style={{ width: `${Math.max(4, t.compliance_pct)}%` }} />
                    </div>
                    <div className={`font-mono-data text-right text-[12.5px] font-bold ${tone.text}`}>{t.compliance_pct}%</div>
                  </div>
                  {t.missing_items.length > 0 && (
                    <div className="mt-1.5 ml-[142px] flex flex-wrap gap-1">
                      {t.missing_items.map((m) => (
                        <span key={m}
                              className="text-[9.5px] uppercase tracking-widest font-mono-data bg-[#fee2e2] text-[#991b1b] border border-[#f87171] rounded px-1.5 py-0.5 font-bold">
                          {m}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Weekly Revenue chart */}
        <section data-testid="wall-weekly-revenue">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-1 h-4 rounded-sm bg-[#0072bc]" />
            <span className="text-[11px] uppercase tracking-[.09em] text-[#0e1420] font-bold">Weekly Revenue</span>
          </div>
          <div className="bg-white border border-[#e7e9ee] rounded-xl p-4 space-y-2">
            {data.weekly_revenue.map((r) => {
              const techMeta = data.techs.find((t) => t.tech_name === r.tech_name);
              const noJobs = techMeta ? techMeta.no_jobs : false;
              return (
                <div key={r.tech_name} className={`grid grid-cols-[130px_1fr_100px] items-center gap-3 ${noJobs ? "opacity-70" : ""}`}>
                  <div className="text-[13px] text-[#0e1420] truncate font-semibold">
                    {r.tech_name}
                    {noJobs && <span className="ml-1.5 text-[9px] font-mono-data uppercase tracking-widest text-[#4b5563]">· no jobs</span>}
                  </div>
                  <div className="h-[14px] rounded-full bg-[#eef1f6] overflow-hidden">
                    <div className="h-full bg-[#0072bc]" style={{ width: `${Math.max(4, (r.revenue / maxRev) * 100)}%` }} />
                  </div>
                  <div className="font-mono-data font-bold text-right text-[13px] text-[#0072bc]">
                    {fmt$(r.revenue)}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Meeting Attendance — click a pill to cycle Not marked → Present → Absent → Not marked */}
        <section data-testid="wall-attendance">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-1 h-4 rounded-sm bg-[#0072bc]" />
            <span className="text-[11px] uppercase tracking-[.09em] text-[#0e1420] font-bold">Meeting Attendance</span>
            <span className="text-[11px] uppercase tracking-[.09em] text-[color:var(--cc-ink-muted)] font-semibold">
              · {data.meeting_attendance.present} of {data.meeting_attendance.marked ?? data.meeting_attendance.total} marked present
              {data.meeting_attendance.total > (data.meeting_attendance.marked ?? data.meeting_attendance.total) && (
                <span className="ml-1 text-[color:var(--cc-ink-muted)]">
                  · {data.meeting_attendance.total - (data.meeting_attendance.marked ?? 0)} not marked
                </span>
              )}
            </span>
            {canEditAttendance && (
              <span className="text-[10px] font-mono-data uppercase tracking-widest text-[color:var(--cc-ink-muted)] ml-1">
                · click to toggle
              </span>
            )}
          </div>
          <div className="bg-white border border-[#e7e9ee] rounded-xl p-4 flex flex-wrap gap-2">
            {data.meeting_attendance.attendees.map((a) => (
              <AttendancePill
                key={a.tech_name}
                techName={a.tech_name}
                present={a.present}
                weekOf={data.week_of}
                canEdit={canEditAttendance}
                onSaved={() => load(week)}
              />
            ))}
          </div>
        </section>

        {/* MRE Service Professional Metrics — feature-flagged behind
            REACT_APP_MRE_SP_METRICS_ENABLED (default off, 2026-07-24 per
            Brett). Turn it on with env: REACT_APP_MRE_SP_METRICS_ENABLED=true.
            Loads independently (its own useEffect) so a slow PATCH here
            can't stall the main wall. Original Phase 1 manual entry
            landed 2026-07-21 per PRD; hidden pending scope decision
            (finish Phase 2 wiring, keep hidden, or remove entirely). */}
        {process.env.REACT_APP_MRE_SP_METRICS_ENABLED === "true" && (
          <section data-testid="wall-mre-sp-metrics-section">
            <MREServiceProMetrics />
          </section>
        )}
        </>)}
      </div>
      <TechPickerPalette
        open={paletteOpen}
        techs={data.techs}
        weekLabel={data.week_label}
        onClose={() => setPaletteOpen(false)}
      />
    </div>
  );
}
